var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import { useEffect, useReducer, useCallback, useState, useMemo } from 'react';
import { useHistory } from 'react-router';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { AuthContext } from './AuthContext';
import { AuthWrapper } from './AuthWrapper';
import { LoadingPage } from './LoadingPage';
import { initialAuthState, authReducer } from './reducer';
import { loginError, hasAuthParams, tokenError, getAuth0Configuration, setDomainScopeCookie, getDomainScopes, removeDomainScopesCookie } from './utils';
/**
 * This component handles all the authentication logic.
 * It should be used only once in an Application and it needs to be used inside a Router instance.
 */
export var AuthProvider = function (_a) {
    var children = _a.children, scopes = _a.scopes, _b = _a.usesPopupLogin, usesPopupLogin = _b === void 0 ? false : _b, callbackUrl = _a.callbackUrl, _c = _a.redirectToAfterLogout, redirectToAfterLogout = _c === void 0 ? window.location.origin : _c, redirectToAfterLoginWithDomainScope = _a.redirectToAfterLoginWithDomainScope, _d = _a.loadingComponent, loadingComponent = _d === void 0 ? React.createElement(LoadingPage, null) : _d;
    var _e;
    var history = useHistory();
    var authClientConfig = getAuth0Configuration({ callbackUrl: callbackUrl, scopes: scopes });
    var _f = __read(useState(function () { return new Auth0Client(authClientConfig); }), 1), auth = _f[0];
    var _g = __read(useReducer(authReducer, initialAuthState), 2), state = _g[0], dispatch = _g[1];
    var userUpdatedAt = (_e = state.user) === null || _e === void 0 ? void 0 : _e.updated_at;
    var usesShortToken = useMemo(function () { return authClientConfig.advancedOptions.defaultScope.includes('short'); }, [authClientConfig.advancedOptions.defaultScope]);
    useEffect(function () {
        var authenticate = function () { return __awaiter(void 0, void 0, void 0, function () {
            var appState, user, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 6, , 7]);
                        if (!hasAuthParams()) return [3 /*break*/, 2];
                        return [4 /*yield*/, auth.handleRedirectCallback()];
                    case 1:
                        appState = (_a.sent()).appState;
                        history.replace((appState === null || appState === void 0 ? void 0 : appState.returnTo) || window.location.pathname);
                        return [3 /*break*/, 4];
                    case 2: return [4 /*yield*/, auth.checkSession()];
                    case 3:
                        _a.sent();
                        _a.label = 4;
                    case 4: return [4 /*yield*/, auth.getUser()];
                    case 5:
                        user = _a.sent();
                        dispatch({ type: 'INITIALISED', user: user });
                        return [3 /*break*/, 7];
                    case 6:
                        error_1 = _a.sent();
                        dispatch({ type: 'ERROR', error: loginError(error_1) });
                        return [3 /*break*/, 7];
                    case 7: return [2 /*return*/];
                }
            });
        }); };
        authenticate();
    }, [auth]);
    var getAccessToken = useCallback(function (options) { return __awaiter(void 0, void 0, void 0, function () {
        var token, error_2, user;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, auth.getTokenSilently(options)];
                case 1:
                    token = _a.sent();
                    return [3 /*break*/, 3];
                case 2:
                    error_2 = _a.sent();
                    throw tokenError(error_2);
                case 3: return [4 /*yield*/, auth.getUser()];
                case 4:
                    user = _a.sent();
                    if ((user === null || user === void 0 ? void 0 : user.updated_at) !== userUpdatedAt) {
                        dispatch({ type: 'USER_UPDATED', user: user });
                    }
                    return [2 /*return*/, token];
            }
        });
    }); }, [auth, userUpdatedAt]);
    var logout = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    sessionStorage.clear();
                    localStorage.clear();
                    removeDomainScopesCookie();
                    return [4 /*yield*/, auth.logout({ returnTo: redirectToAfterLogout })];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [auth, redirectToAfterLogout]);
    var loginWithDomainScope = useCallback(function (domainScope, skipRedirect) {
        setDomainScopeCookie(domainScope);
        // since we get the domain scopes in default config coming from {getAuth0Configuration}
        // we don't need to re-login because every redirect/refresh
        // or new tab opened is considered a new login attempt in a SPA
        if (redirectToAfterLoginWithDomainScope && !skipRedirect) {
            window.location.href = redirectToAfterLoginWithDomainScope;
        }
        else {
            window.location.reload();
        }
    }, [redirectToAfterLoginWithDomainScope]);
    var loginWithRedirect = useCallback(function (opts) { return auth.loginWithRedirect(opts); }, [auth]);
    var loginWithPopup = useCallback(function (opts) { return __awaiter(void 0, void 0, void 0, function () {
        var error_3, user;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, auth.loginWithPopup(opts)];
                case 1:
                    _a.sent();
                    return [3 /*break*/, 3];
                case 2:
                    error_3 = _a.sent();
                    dispatch({ type: 'ERROR', error: loginError(error_3) });
                    return [2 /*return*/];
                case 3: return [4 /*yield*/, auth.getUser()];
                case 4:
                    user = _a.sent();
                    dispatch({ type: 'INITIALISED', user: user });
                    return [2 /*return*/];
            }
        });
    }); }, [auth]);
    var getClaims = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var accessToken, claims;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, getAccessToken()];
                case 1:
                    accessToken = _a.sent();
                    if (!accessToken)
                        return [2 /*return*/, null];
                    claims = JSON.parse(window.atob(accessToken.split('.')[1]));
                    return [2 /*return*/, {
                            userId: claims['http://lucid/user_id'],
                            tokenId: claims['http://lucid/token_id']
                        }];
            }
        });
    }); }, [getAccessToken]);
    var value = __assign(__assign({}, state), { loadingComponent: loadingComponent,
        getAccessToken: getAccessToken,
        logout: logout,
        loginWithRedirect: loginWithRedirect,
        loginWithPopup: loginWithPopup,
        getDomainScopes: getDomainScopes,
        loginWithDomainScope: loginWithDomainScope,
        getClaims: getClaims });
    return (React.createElement(AuthContext.Provider, { value: value },
        React.createElement(AuthWrapper, { usesPopupLogin: usesPopupLogin, usesShortToken: usesShortToken }, children)));
};
//# sourceMappingURL=AuthProvider.js.map